import { Component, computed, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {
  AnalyticsService,
  BreakpointService,
  ConfirmPopupComponent,
  HeaderService,
  LocalStorageManagerService,
  SignalRActivityTrackerService,
  SpinnerService,
  ViewportHeightService
} from '@zayan-erp/shared';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UserAuthenticationService, User, UserActivityTracker } from '@zayan-erp/administration';
import { format } from 'date-fns';
import { LottieComponent, AnimationOptions } from 'ngx-lottie';
import { filter } from 'rxjs';
import { ToastModule } from '@openng/optimus-ui/toast';

const TRACKED_RECENT_MODULES = new Set([
  'all-files',
  'warranty',
  'administration',
  'scheduler',
  'home-building',
  'accounting',
  'sales',
  'dsm',
  'buyer',
  'executives'
]);

@Component({
  imports: [RouterOutlet, ConfirmPopupComponent, LottieComponent, ToastModule],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  route = inject(Router);
  destroyRef = inject(DestroyRef);
  spinnerService = inject(SpinnerService);
  router = inject(Router);
  headerService = inject(HeaderService);
  breakpointService = inject(BreakpointService);
  private analyticsService = inject(AnalyticsService);
  #localStorageService = inject(LocalStorageManagerService);

  isMobile = toSignal(this.breakpointService.isMobile$);
  isBlockedUIActive = computed(() => this.spinnerService.isSpinnerRunning());
  currentDate = format(new Date(), 'yyyy-MM-dd');
  title = 'Login';
  currentPath = '';

  private resizeTimeout: any;

  constructor(
    private viewportHeightService: ViewportHeightService,
    private _authService: UserAuthenticationService,
    private _signalRActivityTrackerService: SignalRActivityTrackerService
  ) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed() // Auto-unsubscribes on destroy, no need for OnDestroy
      )
      .subscribe((event: NavigationEnd) => {
        const newPath = event.urlAfterRedirects.split('?')[0];
        if (newPath !== this.currentPath) {
          this.currentPath = newPath; // updated here after every real path change
          this.headerService.clearSearchConfig();
          this.trackRecentScreen(newPath);
        }
        this.analyticsService.capturePageView(event.urlAfterRedirects);
      });
  }

  /** Remember the last few module screens for the welcome-page "Recent" pills. */
  private trackRecentScreen(path: string): void {
    const segment = path.split('/').filter(Boolean)[0];
    if (!segment || !TRACKED_RECENT_MODULES.has(segment)) return;
    this.#localStorageService.upsertRecentScreen(path, segment);
  }

  ngOnInit(): void {
    // Set the dynamic height when the component initializes
    this.viewportHeightService.setDynamicHeight();

    this.initializeSignalR();
  }

  private initializeSignalR(): void {
    this._signalRActivityTrackerService
      .startConnection()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const user = this._authService.getUser();

          const activityData = this.createActivityTrackerDto(
            user,
            this.route.routerState.snapshot.url,
            this.route.routerState.snapshot.root.data['title'] || 'Unknown Title'
          );

          this._signalRActivityTrackerService.pushActivityTrackerData(activityData);
        }
      });

    this.route.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const user = this._authService.getUser();

        let currentRoute = this.route.routerState.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }

        const symbols = Object.getOwnPropertySymbols(currentRoute.snapshot.data);
        const pageTitle = symbols.length > 0 ? currentRoute.snapshot.data[symbols[0]] : 'Unknown Title';

        const activityData = this.createActivityTrackerDto(user, event.url, pageTitle);

        if (activityData.pageTitle === 'Login' || activityData.pageTitle === 'OTP Verification') return;

        this._signalRActivityTrackerService.pushActivityTrackerData(activityData);
      }
    });

    this._signalRActivityTrackerService.logoutUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        const user = this._authService.getUser();
        if (user.id === response.userId) {
          this._authService.logout();
        }
      }
    });
  }

  createActivityTrackerDto(user: User, pageUrl: string, pageTitle: string): UserActivityTracker {
    const activityData: UserActivityTracker = { ...user, pageUrl, pageTitle };
    return activityData;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    // Clear the previous timeout
    clearTimeout(this.resizeTimeout);

    // Set a new timeout to call the resize handler after 200ms
    this.resizeTimeout = setTimeout(() => {
      this.viewportHeightService.setDynamicHeight();
    }, 210);
  }

  options: AnimationOptions = {
    path: 'assets/images/lottie-animations/loading.json'
  };
}
